import ReportTable from "../ReportTable.component";
import { useEffect, useState, useMemo, useRef } from "react";
import ReportModal from "../ReportModal.component";
import ReportService from "../../../services/reports/Report.service";
import BaseReport from "../BaseReport.component";
import { ClassPerformanceRawData, ClassPerformanceParsedData } from "../../../interfaces/reports/ClassPerformanceReport.interface";
import { MoneyDataItem, PercentDataItem, StringDataItem } from "../../../interfaces/reports/BaseReport.interface";
import TipContext from "../../../providers/GlobalTip";
import EpochMonthService from "../../../services/EpochMonth.service";
import MonthSelector from "../../../tailwindComponents/ui/dates/month-selector.component";

interface ClassPerformanceReportProps {
    clientId: string | number;
    epochMonth: number | null;
    // Optional overrides for custom clients
    hierarchyOverride?: Record<string, string[]>;
    fetchDataFn?: (args: { clientId: string | number; startEpochMonth: number; endEpochMonth: number; isMtd: boolean }) => Promise<{ data?: any[]; parentLevelData?: any[] } | undefined>;
    parseDataFn?: (raw: ClassPerformanceRawData[]) => ClassPerformanceParsedData[];
    // New: allow injecting additional selection controls to show inline with base controls
    extraControls?: React.ReactNode;
    defaultHierarchy?: string[];
}

const HIERARCHY_OPTIONS = {
    "By Location": ["locationCode"],
    "Location By Class": ["locationCode", "classCode"],
    "Location by Class by Child": ["locationCode", "parentClientClassCode", "classCode"],
    "Location By Category": ["locationCode", "category"],
    "Location by Category by Class": ["locationCode", "category", "classCode"],
    "By Class": ["classCode"],
    "Class by Child": ["parentClientClassCode", "classCode"],
    "Class by Child by Location": ["parentClientClassCode", "classCode", "locationCode"],
    "Class by Location": ["classCode", "locationCode"],
    "By Category": ["category"],
    "Category by Location": ["category", "locationCode"],
    "Category by Class": ["category", "classCode"],
    "Category by Class by Location": ["category", "classCode", "locationCode"],
    "Category by Class by Child": ["category", "parentClientClassCode", "classCode"],
}
 
// Moved data processing to a separate function
const processRawData = (rawData: ClassPerformanceRawData[]): ClassPerformanceParsedData[] => {
    if (!rawData || rawData.length === 0) return [];

    // Parse the raw data into controlled and formattable types
    return rawData.map((item) => {
        const markdownPercent = (item.sales && item.markdowns) ? (item.markdowns / item.sales) : 0;
        return {
            locationCode: item.locationCode,
            locationName: item.locationName,
            classCode: item.classCode,
            className: item.className,
            parentClientClassCode: item.parentClientClassCode ?? null,
            category: item.category ? item.category == "" ? "N/A" : item.category : "N/A",
            categorySortOrder: item.categorySortOrder ?? null,

            sales: new MoneyDataItem(item.sales),
            salesPercentToGroup: null,
            salesPercentToTotal: null,
            inventory: new MoneyDataItem(item.inventory),
            inventoryPercentToGroup: null,
            inventoryPercentToTotal: null,
            inflowRetail: new MoneyDataItem(item.inflowRetail),
            inflowRetailPercentToGroup: null,
            inflowRetailPercentToTotal: null,
            inflowCost: new MoneyDataItem(item.inflowCost),
            markdowns: new MoneyDataItem(item.markdowns ?? 0),
            cop: new PercentDataItem(ReportService.calculateCop({ inflowCost: item.inflowCost ?? 0, sales: item.sales ?? 0 }) * 100),
            mmu: new PercentDataItem(ReportService.calculateMmu({ receivedRetailAcrossLocations: item.receivedRetailAcrossLocations ?? 0, receivedCostAcrossLocations: item.receivedCostAcrossLocations ?? 0, markdownPercent }) * 100),
            imu: new PercentDataItem(ReportService.calculateImu({ receivedRetailAcrossLocations: item.receivedRetailAcrossLocations ?? 0, receivedCostAcrossLocations: item.receivedCostAcrossLocations ?? 0 }) * 100),
            markdownPercentage: new PercentDataItem(markdownPercent * 100),

            // Not columns, used for calculations
            receivedCostAcrossLocations: new MoneyDataItem(item.receivedCostAcrossLocations ?? 0),
            receivedRetailAcrossLocations: new MoneyDataItem(item.receivedRetailAcrossLocations ?? 0),
        }
    });
};

const reapplyPercentCalcs = (data: ClassPerformanceParsedData[]) => {
    // Reapply percent calculations based on the hierarchy groups
    return data.map(item => {
        // The percent calculations will be handled by ReportService grouping
        // Just recalculate the derived metrics
        const markdownPercent =
            item.sales && item.sales.value && item.markdowns?.value
                ? (Number(item.markdowns.value) || 0) / (Number(item.sales.value) || 1)
                : 0;

        return {
            ...item,
            cop: new PercentDataItem(
                ReportService.calculateCop({
                    inflowCost: Number(item.inflowCost?.value) || 0,
                    sales: Number(item.sales?.value) || 0
                }) * 100
            ),
            mmu: new PercentDataItem(
                ReportService.calculateMmu({
                    receivedRetailAcrossLocations: Number(item.receivedRetailAcrossLocations?.value) || 0,
                    receivedCostAcrossLocations: Number(item.receivedCostAcrossLocations?.value) || 0,
                    markdownPercent
                }) * 100
            ),
            imu: new PercentDataItem(
                ReportService.calculateImu({
                    receivedRetailAcrossLocations: Number(item.receivedRetailAcrossLocations?.value) || 0,
                    receivedCostAcrossLocations: Number(item.receivedCostAcrossLocations?.value) || 0
                }) * 100
            ),
            markdownPercentage: new PercentDataItem(markdownPercent * 100)
        };
    });
}

const ClassPerformanceReport = ({ clientId, epochMonth, hierarchyOverride, fetchDataFn, parseDataFn, extraControls, defaultHierarchy }: ClassPerformanceReportProps) => {
    const globalTip = TipContext.useMessage();
    const dataRef = useRef<ClassPerformanceRawData[]>([]);
    const parentDataRef = useRef<ClassPerformanceRawData[]>([]);
    const [parsedData, setParsedData] = useState<ClassPerformanceParsedData[]>([]);
    const [parentParsedData, setParentParsedData] = useState<ClassPerformanceParsedData[]>([]);
    const [sortedData, setSortedData] = useState<ClassPerformanceParsedData[]>([]);
    const [loading, setLoading] = useState(true);
    const [hierarchy, setHierarchy] = useState<string[] | null>(defaultHierarchy ?? ["locationCode", "classCode"]);
    const [trendType, setTrendType] = useState<'MTD' | 'EOM'>('MTD');
    const [startEpochMonth, setStartEpochMonth] = useState<number>(epochMonth ? epochMonth - 12 : EpochMonthService.getTodayEpochMonth() - 12);
    const [endEpochMonth, setEndEpochMonth] = useState<number>(epochMonth ?? EpochMonthService.getTodayEpochMonth());

    // Filters
    const [locationFilter, setLocationFilter] = useState<string[]>([]);
    const [classFilter, setClassFilter] = useState<string[]>([]);
    const [categoryFilter, setCategoryFilter] = useState<string[]>([]);

    let hierarchyColumns = [];
    for (let i = 0; i < (hierarchy?.length ?? 0); i++) {
        hierarchyColumns.push({
            name: "",
            sortable: false,
            customClassName: i === 0
                ? "px-4 py-2 bg-gray-500 text-white"
                : "px-4 py-2 bg-purple-100 border-purple-700 text-purple-800"
        });
    }

    const columns = [
        ...hierarchyColumns,
        { name: "Sales", sortable: true },
        { name: "% of Group", sortable: false },
        { name: "% of Total", sortable: false },
        { name: "Inventory", sortable: true },
        { name: "% of Group", sortable: false },
        { name: "% of Total", sortable: false },
        { name: "Inflow Retail", sortable: true },
        { name: "% of Group", sortable: false },
        { name: "% of Total", sortable: false },
        { name: "Inflow Cost", sortable: true },
        { name: "COP%", sortable: true },
        { name: "IMU%", sortable: true },
        { name: "MKDN%", sortable: true },
        { name: "MMU%", sortable: true },
    ];

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const args = { clientId, startEpochMonth, endEpochMonth, isMtd: trendType == 'MTD' };
            const request = fetchDataFn
                ? fetchDataFn(args)
                : ReportService.getClassPerformanceReportData(args);
            request.then((response) => {
                if (response) {
                    // Store raw data in refs
                    dataRef.current = response.data ?? [];
                    parentDataRef.current = response.parentLevelData ?? [];

                    // Process both child and parent data
                    const processed = (parseDataFn ?? processRawData)(response.data ?? []);
                    const processedParent = (parseDataFn ?? processRawData)(response.parentLevelData ?? []);
                    setParsedData(processed);
                    setParentParsedData(processedParent);

                    // Default: use child data
                    let sorted = ReportService.groupByHierarchy(processed, hierarchy ?? ["locationCode", "classCode"]);
                    sorted = reapplyPercentCalcs(sorted);
                    setSortedData(sorted);
                } else {
                    dataRef.current = [];
                    parentDataRef.current = [];
                    setParsedData([]);
                    setParentParsedData([]);
                    setSortedData([]);
                }
                setLoading(false);
            }).catch((error) => {
                if (globalTip)
                    globalTip.show("Failed to fetch data");
                setLoading(false);
            });
        };
        fetchData();
    }, [clientId, startEpochMonth, endEpochMonth, trendType]);

    useEffect(() => {
        setStartEpochMonth(endEpochMonth - 12);
    }, [endEpochMonth]);

    useEffect(() => {
        // Use parent or child data based on hierarchy
        const useParent = hierarchy?.includes("parentClientClassCode");
        const sourceData = useParent ? parentParsedData : parsedData;

        if (sourceData.length > 0) {
            let newSortedData = ReportService.sortAndFilterData(
                sourceData,
                hierarchy ?? ["locationCode", "classCode"],
                locationFilter,
                classFilter,
                categoryFilter
            );
            newSortedData = reapplyPercentCalcs(newSortedData);
            setSortedData(newSortedData);
        }
    }, [hierarchy, parsedData, parentParsedData, locationFilter, classFilter, categoryFilter]);

    const tableData = useMemo(() => {
        // Carrying variables for totals at different hierarchy levels
        let grandTotal = {
            sales: 0,
            inventory: 0,
            inflowRetail: 0
        };
        let groupTotal1 = {
            sales: 0,
            inventory: 0,
            inflowRetail: 0
        };
        let groupTotal2 = {
            sales: 0,
            inventory: 0,
            inflowRetail: 0
        };

        return sortedData.map((item, index) => {
            const labelRows = ReportService.hierarchyItemToVerticalDisplayNames(item, hierarchy ?? ["locationCode", "classCode"]);
            const totalCount = labelRows.filter(label => label.includes("TOTAL")).length;
            const hierarchyLength = hierarchy?.length ?? 2;

            // Update carrying totals based on total count
            if (totalCount === hierarchyLength) {
                // Grand total
                grandTotal = {
                    sales: Number(item.sales?.value) || 0,
                    inventory: Number(item.inventory?.value) || 0,
                    inflowRetail: Number(item.inflowRetail?.value) || 0
                }; 
            } else if (totalCount === hierarchyLength - 1) {
                // Group total 1
                groupTotal1 = {
                    sales: Number(item.sales?.value) || 0,
                    inventory: Number(item.inventory?.value) || 0,
                    inflowRetail: Number(item.inflowRetail?.value) || 0
                };
            } else if (totalCount === hierarchyLength - 2 && hierarchyLength >= 3) {
                // Group total 2 (only for hierarchy length 3)
                groupTotal2 = {
                    sales: Number(item.sales?.value) || 0,
                    inventory: Number(item.inventory?.value) || 0,
                    inflowRetail: Number(item.inflowRetail?.value) || 0
                };
            }

            // Calculate %toGroup based on total count
            let salesPercentToGroup = "100%";
            let inventoryPercentToGroup = "100%";
            let inflowRetailPercentToGroup = "100%";

            if (totalCount === 0) {
                // Use the lowest level carrying total available
                const useTotal = hierarchyLength === 1 ? grandTotal :
                               hierarchyLength === 2 ? groupTotal1 :
                               groupTotal2.sales ? groupTotal2 : groupTotal1;

                salesPercentToGroup = useTotal.sales ?
                    ((Number(item.sales?.value) || 0) / useTotal.sales * 100).toFixed(2) + "%" : "";
                inventoryPercentToGroup = useTotal.inventory ?
                    ((Number(item.inventory?.value) || 0) / useTotal.inventory * 100).toFixed(2) + "%" : "";
                inflowRetailPercentToGroup = useTotal.inflowRetail ?
                    ((Number(item.inflowRetail?.value) || 0) / useTotal.inflowRetail * 100).toFixed(2) + "%" : "";
            } else if (totalCount === hierarchyLength - 1) {
                // Compare to grand total
                salesPercentToGroup = grandTotal.sales ? 
                    ((Number(item.sales?.value) || 0) / grandTotal.sales * 100).toFixed(2) + "%" : "";
                inventoryPercentToGroup = grandTotal.inventory ? 
                    ((Number(item.inventory?.value) || 0) / grandTotal.inventory * 100).toFixed(2) + "%" : "";
                inflowRetailPercentToGroup = grandTotal.inflowRetail ? 
                    ((Number(item.inflowRetail?.value) || 0) / grandTotal.inflowRetail * 100).toFixed(2) + "%" : "";
            } else if (totalCount === hierarchyLength - 2 && hierarchyLength >= 3) {
                // Compare to group total 1
                salesPercentToGroup = groupTotal1.sales ? 
                    ((Number(item.sales?.value) || 0) / groupTotal1.sales * 100).toFixed(2) + "%" : "";
                inventoryPercentToGroup = groupTotal1.inventory ? 
                    ((Number(item.inventory?.value) || 0) / groupTotal1.inventory * 100).toFixed(2) + "%" : "";
                inflowRetailPercentToGroup = groupTotal1.inflowRetail ? 
                    ((Number(item.inflowRetail?.value) || 0) / groupTotal1.inflowRetail * 100).toFixed(2) + "%" : "";
            }

            // %toTotal always uses grand total
            const calcPercentToTotal = (value: number, total: number) => {
                if (!total) return "";
                const pct = (value / total) * 100;
                return pct === 100 ? `${pct.toFixed(0)}%` : `${pct.toFixed(2)}%`;
            };

            const salesPercentToTotal = calcPercentToTotal(
                Number(item.sales?.value) || 0,
                grandTotal.sales
            );
            const inventoryPercentToTotal = calcPercentToTotal(
                Number(item.inventory?.value) || 0,
                grandTotal.inventory
            );
            const inflowRetailPercentToTotal = calcPercentToTotal(
                Number(item.inflowRetail?.value) || 0,
                grandTotal.inflowRetail
            );

            return [
                ...labelRows,
                item.sales ? item.sales.formatted : "",
                salesPercentToGroup,
                salesPercentToTotal,
                item.inventory ? item.inventory.formatted : "",
                inventoryPercentToGroup,
                inventoryPercentToTotal,
                item.inflowRetail ? item.inflowRetail.formatted : "",
                inflowRetailPercentToGroup,
                inflowRetailPercentToTotal,
                item.inflowCost ? item.inflowCost.formatted : "",
                item.cop ? item.cop.formatted : "",
                item.imu ? item.imu.formatted : "",
                item.markdownPercentage ? item.markdownPercentage.formatted : "",
                item.mmu ? item.mmu.formatted : ""
            ];
        });
    }, [sortedData, hierarchy]);

    const rowClassName = (rowIndex: number) => {
        if (!tableData || rowIndex < 0 || rowIndex >= tableData.length) return null;
        return ReportService.getRowClassName(tableData[rowIndex], hierarchy ?? ["locationCode", "classCode"]);
    };

    return (
        <div className="w-full h-full flex flex-col items-center justify-center text-center">
            <h2 className="text-2xl font-bold mb-4">{`${EpochMonthService.epochMonthToMMMYY(startEpochMonth ?? EpochMonthService.getTodayEpochMonth() - 12)} to ${EpochMonthService.epochMonthToMMMYY(endEpochMonth ?? EpochMonthService.getTodayEpochMonth())} Class Performance Report`}</h2>
            {loading ? (
                <p>Loading...</p>
            ) : (
                <div className="w-full h-full">
                    {/* BaseReport for PDF/CSV generation */}
                    <BaseReport
                        title={`${EpochMonthService.epochMonthToMMMYY(epochMonth ? epochMonth -12 : EpochMonthService.getTodayEpochMonth() - 12)} to ${EpochMonthService.epochMonthToMMMYY(epochMonth ?? EpochMonthService.getTodayEpochMonth())} Class Performance Report`}
                        tableColumns={columns.map((col) => col.name)}
                        tableData={tableData}
                        fileName={`Class_Performance_Report_${new Date().toLocaleDateString()}`}
                        setHierarchy={setHierarchy}
                        hierarchy={hierarchy}
                        hierarchyOptions={hierarchyOverride ?? HIERARCHY_OPTIONS}

                        parsedData={parsedData}
                        filteredLocationCodes={locationFilter}
                        filteredClassCodes={classFilter}
                        setFilteredLocationCodes={setLocationFilter}
                        setFilteredClassCodes={setClassFilter}
                        filteredCategories={categoryFilter}
                        setFilteredCategories={setCategoryFilter}

                        onApplyFilters={() => {
                            // Reparse
                            let parsedData = (parseDataFn ?? processRawData)(dataRef.current);
                            setParsedData(parsedData);
                            let parsedParentData = (parseDataFn ?? processRawData)(parentDataRef.current);
                            setParentParsedData(parsedParentData);
                        }}
                    />
                    <div className="flex flex-row flex-wrap items-center space-x-4 mb-4 ml-8">
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Start Period</span>
                            <MonthSelector epochMonth={startEpochMonth} setEpochMonth={setStartEpochMonth} minMonth={endEpochMonth - 12} maxMonth={endEpochMonth} />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">End Period</span>
                            <MonthSelector epochMonth={endEpochMonth} setEpochMonth={setEndEpochMonth} minMonth={startEpochMonth} maxMonth={startEpochMonth + 12} />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Trend Type</span>
                            <select
                                value={trendType}
                                onChange={e => {
                                    const val = e.target.value;
                                    setTrendType(val === 'MTD' ? 'MTD' : 'EOM');
                                }}
                                className="h-10 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
                            >
                                <option value="MTD">MTD</option>
                                <option value="EOM">EOM</option>
                            </select>
                        </div>
                        {/* Injected extra controls */}
                        {extraControls}
                    </div>
                    
                    {/* Display table with export buttons */}
                    <ReportTable
                        columns={columns}
                        data={tableData}
                        rowClassName={rowClassName}
                        defaultSortColumn="Sales"
                        defaultSortDirection="desc"
                        headerClassName="bg-slate-700 !text-white !text-center py-2 px-4"
                        toPdf={() => (window as any).BaseReportExport && (window as any).BaseReportExport('pdf')}
                        toCsv={() => (window as any).BaseReportExport && (window as any).BaseReportExport('csv')}
                        stickyHeader={true}
                        virtualize={true}
                        batchSize={300}
                        hierarchyDepth={hierarchy ? hierarchy.length : 0}
                    />
                </div>
            )}
        </div>
    );
};

interface ClassPerformanceReportModalProps {
    clientId: string | number;
    epochMonth: number | null;
    onClose: () => void;
}

const ClassPerformanceReportModal = ({ clientId, epochMonth, onClose }: ClassPerformanceReportModalProps) => {
    return (
        <ReportModal
            onClose={onClose}
        >
            <ClassPerformanceReport clientId={clientId} epochMonth={epochMonth} />
        </ReportModal>
    );
};

export {
    ClassPerformanceReport,
    ClassPerformanceReportModal
}